import type { AuditSource, OperatorRole, OperatorStatus } from "@/shared/api/types";
import { formatRelative } from "./dates";

/** State language (design-system §6): words + shape + colour, never colour alone. */
export type StateTone = "success" | "danger" | "warning" | "neutral" | "accent" | "info";

export interface StatePresentation {
  tone: StateTone;
  shape: "filled" | "hollow" | "alert";
  label: string;
}

/** Organisation lifecycle (M5): trial → active | suspended | deleting → deleted; an unknown value reads as itself. */
export function tenantStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "trial":
      return { tone: "info", shape: "filled", label: "Trial" };
    case "active":
      return { tone: "success", shape: "filled", label: "Active" };
    case "suspended":
      return { tone: "danger", shape: "alert", label: "Suspended" };
    case "deleting":
      return { tone: "danger", shape: "alert", label: "Scheduled for deletion" };
    case "deleted":
      return { tone: "neutral", shape: "hollow", label: "Deleted" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** The fleet and tenant device rows carry the device status as a string; the same words as the customer portal. */
export function deviceStatusPresentation(
  status: string,
  lastSeenAt: string | null,
  now: Date = new Date(),
): StatePresentation {
  switch (status) {
    case "online":
      return { tone: "success", shape: "filled", label: "Online" };
    case "offline":
      return {
        tone: "neutral",
        shape: "hollow",
        label: lastSeenAt ? `Offline · ${formatRelative(lastSeenAt, now)}` : "Offline",
      };
    case "auth_failed":
      return { tone: "danger", shape: "alert", label: "Needs attention" };
    case "pending":
      return { tone: "warning", shape: "filled", label: "Waiting for device" };
    case "disabled":
      return { tone: "neutral", shape: "hollow", label: "Disabled" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

export function provisioningPresentation(state: string): StatePresentation {
  switch (state) {
    case "pending":
      return { tone: "neutral", shape: "hollow", label: "Not provisioned yet" };
    case "in_progress":
      return { tone: "warning", shape: "filled", label: "Provisioning…" };
    case "provisioned":
      return { tone: "success", shape: "filled", label: "Provisioned" };
    case "failed":
      return { tone: "danger", shape: "alert", label: "Provisioning failed" };
    default:
      return { tone: "neutral", shape: "hollow", label: state };
  }
}

/** Gateway presence for a terminal (device-gateway rules §3 states), in words. */
export function liveStatePresentation(state: string): StatePresentation {
  switch (state) {
    case "ready":
      return { tone: "success", shape: "filled", label: "Ready" };
    case "connected":
    case "authenticating":
      return { tone: "warning", shape: "filled", label: "Connecting…" };
    case "unclaimed":
      return { tone: "info", shape: "filled", label: "Unclaimed" };
    case "disconnected":
      return { tone: "neutral", shape: "hollow", label: "Disconnected" };
    default:
      return { tone: "neutral", shape: "hollow", label: state };
  }
}

export function operatorRolePresentation(role: OperatorRole): StatePresentation {
  return role === "admin"
    ? { tone: "accent", shape: "filled", label: "Admin" }
    : { tone: "neutral", shape: "filled", label: "Operator" };
}

export function operatorStatusPresentation(status: OperatorStatus): StatePresentation {
  return status === "active"
    ? { tone: "success", shape: "filled", label: "Active" }
    : { tone: "neutral", shape: "hollow", label: "Disabled" };
}

/** Where an audit row came from: the organisation's own log, or the platform log of operator actions. */
export function auditSourcePresentation(source: AuditSource): StatePresentation {
  return source === "platform"
    ? { tone: "accent", shape: "filled", label: "Platform" }
    : { tone: "neutral", shape: "hollow", label: "Tenant" };
}

/**
 * M11 (ADR-0015): an ORGANISATION's subscription state — its one billing period, not a gateway object.
 * `overdue` is the one that matters most on screen: the organisation still works, and somebody has to
 * pay before the grace runs out (runbooks/billing.md lifecycle).
 */
export function subscriptionStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "trial":
      return { tone: "info", shape: "filled", label: "Trial" };
    case "active":
      return { tone: "success", shape: "filled", label: "Active" };
    case "overdue":
      return { tone: "warning", shape: "alert", label: "Overdue" };
    case "suspended":
      return { tone: "danger", shape: "alert", label: "Suspended" };
    case "ended":
      return { tone: "neutral", shape: "hollow", label: "Ended" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** M11: one catalogue item as an organisation holds it (`tenant_items.state`). */
export function itemStatePresentation(state: string, cancelAtPeriodEnd = false): StatePresentation {
  if (cancelAtPeriodEnd && (state === "active" || state === "trial"))
    return { tone: "warning", shape: "filled", label: "Ends at period end" };
  switch (state) {
    case "trial":
      return { tone: "info", shape: "filled", label: "Trial" };
    case "active":
      return { tone: "success", shape: "filled", label: "Active" };
    case "expired":
      return { tone: "neutral", shape: "hollow", label: "Expired" };
    case "cancelled":
      return { tone: "neutral", shape: "hollow", label: "Cancelled" };
    default:
      return { tone: "neutral", shape: "hollow", label: state };
  }
}

/** M11: a `billing_orders` row. `issued` is the only one that is waiting for money. */
export function orderStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "draft":
      return { tone: "neutral", shape: "hollow", label: "Draft" };
    case "issued":
      return { tone: "info", shape: "filled", label: "Awaiting payment" };
    case "paid":
      return { tone: "success", shape: "filled", label: "Paid" };
    case "offline_paid":
      return { tone: "success", shape: "filled", label: "Paid offline" };
    case "expired":
      return { tone: "warning", shape: "alert", label: "Expired" };
    case "cancelled":
      return { tone: "neutral", shape: "hollow", label: "Cancelled" };
    case "reverted":
      return { tone: "danger", shape: "hollow", label: "Reverted" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** M11: what an order is for, in the words the runbook uses. */
export function orderKindLabel(kind: string): string {
  switch (kind) {
    case "purchase":
      return "Purchase";
    case "proration":
      return "Added mid-period";
    case "renewal":
      return "Renewal";
    case "manual":
      return "Added by Nexborg";
    // M20 (ADR-0024): prepaid SMS credits are an order too, with no subscription effect.
    case "sms_recharge":
      return "SMS recharge";
    default:
      return kind;
  }
}

/** M11: a catalogue row's own status, as the editor lists it. */
export function catalogStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "active":
      return { tone: "success", shape: "filled", label: "On sale" };
    case "draft":
      return { tone: "neutral", shape: "hollow", label: "Draft" };
    case "retired":
      return { tone: "warning", shape: "hollow", label: "Retired" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/** M11: a discount rule's own status. */
export function discountStatusPresentation(status: string): StatePresentation {
  return status === "active"
    ? { tone: "success", shape: "filled", label: "Active" }
    : { tone: "neutral", shape: "hollow", label: "Retired" };
}

/** M11: which orders a discount rule may touch (ADR-0015 §2). */
export function discountScopeLabel(scope: string): string {
  switch (scope) {
    case "first_purchase":
      return "First purchase";
    case "renewal":
      return "Renewals";
    case "any":
      return "Every order";
    default:
      return scope;
  }
}

/** M11: the billing period, said the way the customer sees it. */
export function periodLabel(period: string): string {
  return period === "yearly" ? "Yearly" : period === "monthly" ? "Monthly" : period;
}

export function invoiceStatusPresentation(status: string): StatePresentation {
  switch (status) {
    case "paid":
      return { tone: "success", shape: "filled", label: "Paid" };
    case "partially_paid":
      return { tone: "warning", shape: "filled", label: "Partially paid" };
    case "issued":
      return { tone: "info", shape: "filled", label: "Issued" };
    case "draft":
      return { tone: "neutral", shape: "hollow", label: "Draft" };
    case "cancelled":
      return { tone: "neutral", shape: "hollow", label: "Cancelled" };
    case "expired":
      return { tone: "neutral", shape: "hollow", label: "Expired" };
    case "deleted":
      return { tone: "neutral", shape: "hollow", label: "Deleted" };
    default:
      return { tone: "neutral", shape: "hollow", label: status };
  }
}

/**
 * A catalogue code with no name to hand ("people_lot" → "People lot"). Everywhere the catalogue is
 * loaded the item's own `name` is used instead — this is the fallback for a code an old audit row or
 * a retired item still carries.
 */
export function itemCodeLabel(code: string): string {
  if (!code) return "—";
  const words = code.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** A person's display name: the name they set, else their number in mono. */
export function personLabel(name: string | null, mobile: string): string {
  return name || mobile;
}
